import React, { useEffect, useMemo, useState } from "react";
import {
  Bell,
  ChevronRight,
  Download,
  Files,
  Headphones,
  LayoutDashboard,
  Menu,
  Mic,
  Moon,
  PhoneCall,
  PhoneForwarded,
  Plus,
  RadioTower,
  Search,
  Settings,
  Sun,
  UsersRound,
  X,
} from "lucide-react";
import { applyThemePreference, isDarkThemeEnabled } from "../helper/theme";

const brandLogo = `${process.env.PUBLIC_URL || ""}/voipflow-logo.svg`;

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "extensions", label: "Extensions", icon: PhoneCall },
  { key: "dids", label: "DID Numbers", icon: PhoneForwarded },
  { key: "queues", label: "Queues / Ring Groups", icon: UsersRound },
  { key: "trunks", label: "Trunks", icon: RadioTower },
  { key: "cdr", label: "Call Logs (CDR)", icon: Files },
  { key: "recordings", label: "Recordings", icon: Mic },
  { key: "live", label: "Live Calls", icon: Headphones },
  { key: "settings", label: "Settings", icon: Settings },
];

const STATS = [
  { label: "Active Calls", value: "26", delta: "+4", tone: "primary" },
  { label: "Live Routes", value: "18", delta: "Healthy", tone: "success" },
  { label: "Total Calls Today", value: "612", delta: "+9%", tone: "primary" },
  { label: "ASR / ACD", value: "82% / 4m 10s", delta: "Good", tone: "success" },
  { label: "Online Extensions", value: "14 / 16", delta: "87.5%", tone: "warning" },
];

const CALL_FLOW = [
  { step: "1", title: "DID / TFN arrives", text: "Asterisk looks up the tenant route in MySQL realtime." },
  { step: "2", title: "Greeting / IVR", text: "Optional audio plays before the route continues." },
  { step: "3", title: "Queue or Extension", text: "Redis provides fast presence and balance state." },
  { step: "4", title: "Forwarding", text: "Failover goes to another extension, queue, or PSTN number." },
  { step: "5", title: "CDR + Billing", text: "Call end writes CDR, billing, and recording metadata to MySQL." },
];

const EXTENSIONS = [
  { ext: "3011", name: "Main Sales", status: "Online", type: "WebRTC", did: "877-253-9834" },
  { ext: "3012", name: "Support", status: "Online", type: "SIP UDP", did: "844-520-2941" },
  { ext: "3013", name: "Dispatch", status: "Offline", type: "WebRTC", did: "866-410-9921" },
];

const DIDS = [
  { did: "877-253-9834", target: "Queue 2", forwarding: "+1 917 555 0182", ivr: "sales-main.wav" },
  { did: "844-520-2941", target: "3012", forwarding: "+1 646 555 0133", ivr: "billing.wav" },
  { did: "866-410-9921", target: "External", forwarding: "+1 408 555 0194", ivr: "ivr-night.wav" },
];

const QUEUES = [
  { name: "Queue 2", strategy: "ringall", members: ["3011", "3012", "3013"], note: "Best for simultaneous hunt" },
  { name: "VIP Desk", strategy: "leastrecent", members: ["3011", "3014"], note: "Balanced distribution" },
];

const LIVE_CALLS = [
  { caller: "+1 877 253 9834", destination: "3011", duration: "01:22", state: "Talking" },
  { caller: "+1 844 520 2941", destination: "Queue 2", duration: "00:44", state: "Ringing" },
  { caller: "+1 646 555 0144", destination: "Forwarded PSTN", duration: "08:10", state: "Talking" },
];

const CDR_ROWS = [
  { caller: "+18772539834", destination: "3011", duration: "03:11", disposition: "ANSWERED", amount: "$0.14" },
  { caller: "+18445202941", destination: "Queue 2", duration: "00:51", disposition: "NO ANSWER", amount: "$0.03" },
  { caller: "+16465550144", destination: "Forwarded PSTN", duration: "08:10", disposition: "ANSWERED", amount: "$0.61" },
];

const RECORDINGS = [
  { file: "sales-main-1002.wav", summary: "3011 · 03:11 · answered", size: "1.9 MB" },
  { file: "support-1008.wav", summary: "3012 · 12:05 · answered", size: "6.4 MB" },
];

const SETTINGS_GROUPS = [
  { title: "Simple mode", detail: "Show only day-to-day controls: extensions, DID routing, recordings, and live calls." },
  { title: "Advanced mode", detail: "Unlock IP auth, SIP transport, API keys, and queue tuning only when needed." },
  { title: "Security", detail: "JWT roles, strong extension passwords, and IP-restricted management access." },
];

const toneClasses = {
  primary: "bg-dashboard-primary/10 text-dashboard-primary dark:bg-dashboard-primary/20 dark:text-red-100",
  success: "bg-dashboard-success/10 text-dashboard-success dark:bg-dashboard-success/20 dark:text-emerald-200",
  warning: "bg-dashboard-warning/20 text-amber-700 dark:bg-dashboard-warning/25 dark:text-amber-100",
  danger: "bg-dashboard-danger/10 text-dashboard-danger dark:bg-dashboard-danger/20 dark:text-rose-200",
  neutral: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
};

function StatusBadge({ children, tone = "neutral" }) {
  return <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${toneClasses[tone]}`}>{children}</span>;
}

function PanelCard({ title, action, children, className = "" }) {
  return (
    <section className={`panel-surface panel-enter p-5 ${className}`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold text-slate-900 dark:text-white">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function StatCard({ label, value, delta, tone }) {
  return (
    <div className="panel-surface panel-enter p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
          <p className="mt-2 font-display text-3xl font-semibold text-slate-900 dark:text-white">{value}</p>
        </div>
        <StatusBadge tone={tone}>{delta}</StatusBadge>
      </div>
    </div>
  );
}

function MetricRow({ label, value, note }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-50 px-4 py-3 dark:bg-slate-900/60">
      <div>
        <p className="font-medium text-slate-900 dark:text-white">{label}</p>
        {note ? <p className="text-sm text-slate-500 dark:text-slate-400">{note}</p> : null}
      </div>
      <span className="font-display text-lg font-semibold text-slate-900 dark:text-white">{value}</span>
    </div>
  );
}

function DialPad() {
  const [digits, setDigits] = useState("3011");
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"];

  return (
    <PanelCard title="Open Web Phone" action={<StatusBadge tone="success">Ready</StatusBadge>}>
      <div className="space-y-4">
        <div className="rounded-2xl bg-slate-950 px-4 py-5 text-center text-2xl tracking-[0.35em] text-white">
          {digits}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {keys.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setDigits((current) => `${current}${key}`)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-4 text-lg font-semibold text-slate-900 transition hover:-translate-y-0.5 hover:border-dashboard-primary hover:text-dashboard-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              {key}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" className="rounded-2xl bg-dashboard-success px-4 py-3 font-semibold text-white">Call</button>
          <button type="button" className="rounded-2xl bg-dashboard-danger px-4 py-3 font-semibold text-white">Hangup</button>
          <button type="button" className="rounded-2xl border border-slate-200 px-4 py-3 font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">Mute</button>
          <button type="button" className="rounded-2xl border border-slate-200 px-4 py-3 font-medium text-slate-700 dark:border-slate-700 dark:text-slate-200">Hold</button>
        </div>
      </div>
    </PanelCard>
  );
}

function DashboardPage() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {STATS.map((stat) => <StatCard key={stat.label} {...stat} />)}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
        <PanelCard title="Call Flow" action={<StatusBadge tone="primary">Simple routing</StatusBadge>}>
          <div className="space-y-4">
            {CALL_FLOW.map((item) => (
              <div key={item.step} className="flex gap-4 rounded-2xl border border-slate-200/80 p-4 dark:border-slate-800">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-dashboard-primary text-lg font-semibold text-white">
                  {item.step}
                </div>
                <div>
                  <p className="font-semibold text-slate-900 dark:text-white">{item.title}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </PanelCard>

        <PanelCard title="Quick Health" action={<StatusBadge tone="success">All synced</StatusBadge>}>
          <div className="space-y-3">
            <MetricRow label="Routing source" value="MySQL realtime" note="Fast and deterministic" />
            <MetricRow label="Live presence" value="Redis cache" note="No page refresh needed" />
            <MetricRow label="Billing snapshot" value="5 second cache" note="Wallet loads instantly" />
            <MetricRow label="Recordings" value="Enabled" note="Playback and download in panel" />
          </div>
        </PanelCard>
      </div>
    </div>
  );
}

function ExtensionsPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
      <PanelCard title="Extensions" action={<button type="button" className="rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">Create extension</button>}>
        <div className="space-y-4">
          {EXTENSIONS.map((row) => (
            <div key={row.ext} className="rounded-3xl border border-slate-200/80 p-5 dark:border-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-xl font-semibold text-slate-900 dark:text-white">{row.ext} · {row.name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{row.type} · DID {row.did}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={row.status === "Online" ? "success" : "danger"}>{row.status}</StatusBadge>
                  <button type="button" className="rounded-full border border-dashboard-primary px-4 py-2 text-sm font-semibold text-dashboard-primary">Open Web Phone</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <DialPad />
    </div>
  );
}

function NumbersPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
      <PanelCard title="DID Management" action={<button type="button" className="inline-flex items-center gap-2 rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Buy DID</button>}>
        <div className="space-y-4">
          {DIDS.map((did) => (
            <div key={did.did} className="rounded-3xl border border-slate-200/80 p-5 dark:border-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-xl font-semibold text-slate-900 dark:text-white">{did.did}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Greeting {did.ivr}</p>
                </div>
                <StatusBadge tone="primary">{did.target}</StatusBadge>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <MetricRow label="Primary target" value={did.target} />
                <MetricRow label="Forwarding" value={did.forwarding} note="Used on no answer or after hours" />
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <div className="space-y-6">
        <PanelCard title="Forwarding Panel">
          <div className="space-y-3">
            <MetricRow label="Instant route change" value="Extension / Queue / PSTN" />
            <MetricRow label="Fallback order" value="Primary -> failover -> voicemail" />
            <MetricRow label="Validation" value="Blocks broken endpoints" />
          </div>
        </PanelCard>
        <PanelCard title="Greeting / IVR">
          <div className="rounded-3xl border border-dashed border-slate-300 p-5 text-center dark:border-slate-700">
            <p className="font-semibold text-slate-900 dark:text-white">Upload a greeting</p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Simple for users, advanced options hidden until needed.</p>
            <button type="button" className="mt-4 rounded-full border border-dashboard-primary px-4 py-2 text-sm font-semibold text-dashboard-primary">Select audio</button>
          </div>
        </PanelCard>
      </div>
    </div>
  );
}

function QueuesPage() {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      {QUEUES.map((queue) => (
        <PanelCard key={queue.name} title={queue.name} action={<StatusBadge tone="success">{queue.strategy}</StatusBadge>}>
          <div className="space-y-3">
            <MetricRow label="Members" value={queue.members.join(", ")} note={queue.note} />
            <MetricRow label="Validation" value="Healthy" note="No broken endpoints found" />
            <button type="button" className="w-full rounded-2xl bg-dashboard-primary px-4 py-3 font-semibold text-white">Update ring group</button>
          </div>
        </PanelCard>
      ))}
    </div>
  );
}

function TrunksPage() {
  return (
    <PanelCard title="Trunks" action={<StatusBadge tone="primary">Read-only for users</StatusBadge>}>
      <div className="grid gap-4 lg:grid-cols-3">
        <MetricRow label="Primary carrier" value="Flowroute" note="Main outbound trunk" />
        <MetricRow label="Backup carrier" value="Twilio" note="Used when primary is down" />
        <MetricRow label="Access model" value="View only" note="Admin can edit authentication" />
      </div>
    </PanelCard>
  );
}

function CallLogsPage() {
  return (
    <PanelCard title="Call Logs (CDR)" action={<StatusBadge tone="primary">Fast filters</StatusBadge>}>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <th className="pb-3 pr-4">Caller</th>
              <th className="pb-3 pr-4">Destination</th>
              <th className="pb-3 pr-4">Duration</th>
              <th className="pb-3 pr-4">Disposition</th>
              <th className="pb-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            {CDR_ROWS.map((row) => (
              <tr key={`${row.caller}-${row.destination}`} className="border-b border-slate-100 last:border-none dark:border-slate-900">
                <td className="py-4 pr-4 font-medium text-slate-900 dark:text-white">{row.caller}</td>
                <td className="py-4 pr-4">{row.destination}</td>
                <td className="py-4 pr-4">{row.duration}</td>
                <td className="py-4 pr-4">
                  <StatusBadge tone={row.disposition === "ANSWERED" ? "success" : "warning"}>{row.disposition}</StatusBadge>
                </td>
                <td className="py-4 font-semibold text-slate-900 dark:text-white">{row.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PanelCard>
  );
}

function RecordingsPage() {
  return (
    <PanelCard title="Recordings" action={<StatusBadge tone="warning">Storage quota 62%</StatusBadge>}>
      <div className="space-y-3">
        {RECORDINGS.map((recording) => (
          <div key={recording.file} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200/70 p-4 dark:border-slate-800">
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">{recording.file}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{recording.summary} · {recording.size}</p>
            </div>
            <div className="flex gap-2">
              <button type="button" className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold dark:border-slate-700">Play</button>
              <button type="button" className="inline-flex items-center gap-2 rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white"><Download className="h-4 w-4" /> Download</button>
            </div>
          </div>
        ))}
      </div>
    </PanelCard>
  );
}

function LiveCallsPage() {
  return (
    <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
      <PanelCard title="Live Calls" action={<StatusBadge tone="success">Realtime</StatusBadge>}>
        <div className="space-y-4">
          {LIVE_CALLS.map((row) => (
            <div key={`${row.caller}-${row.destination}`} className="rounded-3xl border border-slate-200/80 p-5 dark:border-slate-800">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-display text-xl font-semibold text-slate-900 dark:text-white">{row.caller}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{row.destination}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <StatusBadge tone="primary">{row.duration}</StatusBadge>
                  <StatusBadge tone={row.state === "Talking" ? "success" : "warning"}>{row.state}</StatusBadge>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button type="button" className="rounded-full border border-dashboard-danger px-4 py-2 text-sm font-semibold text-dashboard-danger">Hangup</button>
                <button type="button" className="rounded-full border border-dashboard-primary px-4 py-2 text-sm font-semibold text-dashboard-primary">Whisper</button>
                <button type="button" className="rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">Barge</button>
              </div>
            </div>
          ))}
        </div>
      </PanelCard>

      <PanelCard title="Realtime Engine">
        <div className="space-y-3">
          <MetricRow label="Event source" value="AMI + Redis" note="Socket pushes active calls instantly" />
          <MetricRow label="Presence cache" value="Redis hash" note="Fast extension status updates" />
          <MetricRow label="Supervisor actions" value="Audited" note="Every whisper, barge, hangup is logged" />
        </div>
      </PanelCard>
    </div>
  );
}

function SettingsPage() {
  const [expanded, setExpanded] = useState("Simple mode");
  const current = SETTINGS_GROUPS.find((item) => item.title === expanded) || SETTINGS_GROUPS[0];

  return (
    <div className="grid gap-6 xl:grid-cols-[0.75fr_1.25fr]">
      <PanelCard title="Modes">
        <div className="space-y-3">
          {SETTINGS_GROUPS.map((group) => (
            <button
              key={group.title}
              type="button"
              onClick={() => setExpanded(group.title)}
              className={`flex w-full items-center justify-between rounded-2xl px-4 py-3 text-left transition ${
                group.title === expanded
                  ? "bg-dashboard-primary text-white"
                  : "bg-slate-50 text-slate-700 dark:bg-slate-900/60 dark:text-slate-200"
              }`}
            >
              <span className="font-medium">{group.title}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          ))}
        </div>
      </PanelCard>

      <PanelCard title={current.title} action={<StatusBadge tone="success">Beginner-friendly</StatusBadge>}>
        <div className="space-y-4">
          <p className="leading-7 text-slate-600 dark:text-slate-300">{current.detail}</p>
          <div className="grid gap-3 md:grid-cols-2">
            <MetricRow label="Dark mode" value="Supported" note="Easy on long shifts" />
            <MetricRow label="JWT roles" value="user / reseller / admin" note="Visible tools depend on role" />
            <MetricRow label="IP allow-list" value="Optional" note="Recommended for trunks and admin" />
            <MetricRow label="API access" value="Private only" note="No public management endpoints" />
          </div>
        </div>
      </PanelCard>
    </div>
  );
}

const pageMap = {
  dashboard: DashboardPage,
  extensions: ExtensionsPage,
  dids: NumbersPage,
  queues: QueuesPage,
  trunks: TrunksPage,
  cdr: CallLogsPage,
  recordings: RecordingsPage,
  live: LiveCallsPage,
  settings: SettingsPage,
};

export default function UserWorkspace() {
  const [activePage, setActivePage] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => isDarkThemeEnabled());

  useEffect(() => {
    applyThemePreference(darkMode);
  }, [darkMode]);

  const ActivePage = useMemo(() => pageMap[activePage] || DashboardPage, [activePage]);
  const activeLabel = NAV_ITEMS.find((item) => item.key === activePage)?.label || "Dashboard";

  return (
    <div className="min-h-screen bg-dashboard-bg dark:bg-dashboard-darkBg">
      <div className="flex min-h-screen">
        <aside className={`fixed inset-y-0 left-0 z-40 w-72 transform bg-dashboard-sidebar px-5 py-6 text-white shadow-lift transition duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="overflow-hidden rounded-2xl border border-cyan-500/25 bg-black/40 shadow-[0_12px_30px_rgba(8,56,86,0.28)]">
                <img
                  src={brandLogo}
                  alt="VoIPFlow logo"
                  className="h-11 w-11 object-cover"
                />
              </div>
              <div>
                <p className="font-display text-2xl font-semibold">VoIPFlow Cloud</p>
                <p className="text-sm text-slate-300">User and reseller panel</p>
              </div>
            </div>
            <button type="button" className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="h-6 w-6" />
            </button>
          </div>

          <nav className="mt-8 space-y-2">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              const isActive = item.key === activePage;

              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setActivePage(item.key);
                    setSidebarOpen(false);
                  }}
                  className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left transition ${
                    isActive ? "bg-white/12 text-white" : "text-slate-300 hover:bg-white/6 hover:text-white"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          <div className="mt-8 rounded-3xl bg-white/8 p-4">
            <p className="text-sm font-semibold">Quick tips</p>
            <div className="mt-3 space-y-2 text-sm text-slate-200">
              <p>Keep queues small and healthy.</p>
              <p>Use greeting before forwarding.</p>
              <p>Check live calls before peak traffic.</p>
            </div>
          </div>
        </aside>

        <div className="flex-1">
          <header className="sticky top-0 z-30 border-b border-slate-200/80 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
            <div className="mx-auto flex max-w-[1600px] items-center gap-3 px-4 py-4 sm:px-6">
              <button type="button" className="rounded-2xl border border-slate-200 p-2 lg:hidden dark:border-slate-700" onClick={() => setSidebarOpen(true)}>
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="hidden min-w-[240px] items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-900 md:flex">
                  <Search className="h-4 w-4 text-slate-400" />
                  <input className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" placeholder="Search numbers, extensions, recordings" />
                </div>
                <div>
                  <p className="font-display text-xl font-semibold text-slate-900 dark:text-white">{activeLabel}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Simple controls for routing, recordings, and daily call operations</p>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3">
                <div className="hidden rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700 dark:bg-slate-900 dark:text-slate-100 md:block">
                  14 Extensions Online
                </div>
                <button type="button" className="rounded-full border border-slate-200 p-3 dark:border-slate-700">
                  <Bell className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setDarkMode((current) => !current)} className="rounded-full border border-slate-200 p-3 dark:border-slate-700">
                  {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <div className="rounded-full bg-dashboard-primary px-4 py-2 text-sm font-semibold text-white">
                  US
                </div>
              </div>
            </div>
          </header>

          <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6">
            <div className="mb-6 grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
              <div className="panel-surface panel-enter bg-gradient-to-r from-dashboard-primary to-dashboard-success p-6 text-white">
                <p className="text-sm uppercase tracking-[0.3em] text-white/75">Production-safe preview</p>
                <h1 className="mt-3 font-display text-3xl font-semibold">Clean VoIP workspace for users and resellers</h1>
                <p className="mt-3 max-w-2xl text-sm text-white/85">
                  Important information stays at the top, advanced settings stay tucked away, and everyday tasks like forwarding, recordings, and live calls stay one click away.
                </p>
              </div>

              <div className="panel-surface panel-enter p-6">
                <div className="grid gap-3 sm:grid-cols-2">
                  <MetricRow label="Presence cache" value="Redis" note="Fast extension state loading" />
                  <MetricRow label="Route engine" value="Asterisk realtime" note="No HTTP dialplan hops" />
                  <MetricRow label="Call logs" value="MySQL" note="Persistent and filterable" />
                  <MetricRow label="Security" value="JWT + roles" note="Private API only" />
                </div>
              </div>
            </div>

            <ActivePage />
          </main>
        </div>
      </div>
    </div>
  );
}
